import { ChatTransport } from "./ChatTransport";
import { streamJsonSseWithRetry } from "../../../../utils/SseStreamUtil";

export const BROWSER_SESSION_EVENTS_PATH = (baseUrl, id) =>
  `${baseUrl}/ai/agent/session/${id}/events/v3`;
export const BROWSER_SESSION_STREAM_PATH = (baseUrl, id, after) =>
  `${baseUrl}/ai/agent/session/${id}/stream/v3?after=${after}`;
export const PUBLIC_CHAT_EVENTS_PATH = (baseUrl, token) =>
  `${baseUrl}/public/ai/agent/public-chat/session/${token}/events/v3`;
export const PUBLIC_CHAT_STREAM_PATH = (baseUrl, token, after) =>
  `${baseUrl}/public/ai/agent/public-chat/session/${token}/stream/v3?after=${after}`;

export class DefaultChatTransport extends ChatTransport {
  constructor({ baseUrl, onEvent, onConnectionStateChange, onStreamDiagnostic, skipAuth = false }) {
    super();
    this._baseUrl = baseUrl;
    this._onEvent = onEvent;
    this._onConnectionStateChange = onConnectionStateChange;
    this._onStreamDiagnostic = onStreamDiagnostic;
    this._skipAuth = skipAuth;
    this._abortController = null;
    this._lastEventId = 0;
    this._connectId = null;
  }

  async connect(id, options = {}) {
    this.disconnect();
    this._connectId = id;
    this._lastEventId = 0;
    this._abortController = new AbortController();

    const url = this._skipAuth
      ? PUBLIC_CHAT_EVENTS_PATH(this._baseUrl, id)
      : BROWSER_SESSION_EVENTS_PATH(this._baseUrl, id);
    return this._stream(url, options);
  }

  async resume(id, lastEventId, options = {}) {
    this.disconnect();
    this._connectId = id;
    this._lastEventId = lastEventId ?? 0;
    this._abortController = new AbortController();

    const url = this._skipAuth
      ? PUBLIC_CHAT_STREAM_PATH(this._baseUrl, id, this._lastEventId)
      : BROWSER_SESSION_STREAM_PATH(this._baseUrl, id, this._lastEventId);
    return this._stream(url, options);
  }

  disconnect() {
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
    this._connectId = null;
  }

  get lastEventId() {
    return this._lastEventId;
  }

  async _stream(url, options) {
    const { shouldReconnect, isSessionTerminal, maxRetries = 5 } = options;
    const controller = this._abortController;
    const connectId = this._connectId;
    let hasCursor = this._lastEventId > 0;
    let completed = false;
    const isCurrent = () => this._abortController === controller && !controller.signal.aborted;

    return streamJsonSseWithRetry({
      url,
      abortController: controller,
      maxRetries,
      logLabel: this._skipAuth ? "PublicChatTransport SSE" : "ChatTransport SSE",
      skipAuth: this._skipAuth,
      isComplete: () => completed || isSessionTerminal?.() === true,
      onEvent: (eventName, data, eventId) => {
        if (!isCurrent()) return;
        const parsed = eventId != null ? Number(eventId) : NaN;
        const seq = Number.isFinite(parsed) ? parsed : null;
        if (eventName === "snapshot" && data?.streamVersion === "v3") {
          const projection = this._onEvent?.(eventName, data, seq);
          completed = projection?.completed === true;
          this._lastEventId = data.throughSequence ?? seq ?? 0;
          hasCursor = true;
          return;
        }
        if (hasCursor && seq != null && seq > this._lastEventId + 1) {
          this._onStreamDiagnostic?.({ event: "sequence_gap", expected: this._lastEventId + 1, received: seq });
          throw new Error("Chat stream gap; reconnecting from the last applied event");
        }
        // Replayed delivery must not append the same text delta twice.
        if (seq != null && seq > 0 && seq <= this._lastEventId) return;
        let projection;
        if (this._onEvent) {
          // Forward the backend-minted monotonic seq (SSE `id:`) so the store
          // can sequence-gate lifecycle transitions. Previously dropped here.
          projection = this._onEvent(eventName, data, seq);
        }
        if (seq != null && seq > this._lastEventId) this._lastEventId = seq;
        // Record completion only after the event passed ordering and was applied.
        // ui_chunk.finish ends a provider step, not necessarily the whole turn.
        // The projection owns completion, including optimistic commands and
        // bootstraps. Raw-event fallback supports transport-only consumers.
        if (typeof projection?.completed === "boolean") {
          completed = projection.completed;
        } else if ((data?.type || eventName) === "session_complete" &&
            ["COMPLETED", "CANCELLED", "ERROR"].includes(data?.payload?.status || data?.status || "COMPLETED")) {
          completed = true;
        }
      },
      shouldReconnect: () => {
        if (!isCurrent() || completed) return false;
        return typeof shouldReconnect === "function" ? shouldReconnect() : false;
      },
      onConnectionStateChange: (state) => {
        if (isCurrent() && this._onConnectionStateChange) this._onConnectionStateChange(state);
      },
      onStreamDiagnostic: (info) => {
        if (isCurrent()) this._onStreamDiagnostic?.(info);
      },
      getReconnectUrl: () => {
        const lastEventId = this._lastEventId;
        if (isCurrent() && lastEventId > 0 && connectId) {
          return this._skipAuth
            ? PUBLIC_CHAT_STREAM_PATH(this._baseUrl, connectId, lastEventId)
            : BROWSER_SESSION_STREAM_PATH(this._baseUrl, connectId, lastEventId);
        }
        return null;
      },
    });
  }
}
